/**
 * src/services/nudges.ts
 *
 * Smart Nudge Scheduler — PWA port of mobile/services/nudges.ts
 *
 * Five contextual nudges, tracked in localStorage:
 *
 *  idle-signals         10:00–11:00  You have pending signals and no focus session yet.
 *  focus-gap            11:00–12:00  2h into workday, zero timer progress on any signal.
 *  midday-snr           12:00–13:00  Noon SNR check — below target threshold.
 *  eod-review           (workdayEnd - 1h)  Time to do your Daily Review.
 *  squad-accountability any time     A squad member has been in session 1h+ and you haven't started.
 *                                    Fires at most once per member per hour (separate storage key).
 *
 * Platform differences from mobile:
 *  - AsyncStorage → localStorage (sync, so no await needed for reads)
 *  - expo-notifications → Web Notifications API (Notification constructor)
 *  - Permission request is non-blocking; nudges silently skip if denied
 *
 * Scheduler: runs a 60-second interval that evaluates conditions. Safe to
 * call initNudgeScheduler() multiple times — idempotent.
 */

import { useTasks } from '../stores/useTasks'
import { useSettings } from '../stores/useSettings'
import { useSNR } from '../stores/useSNR'
import { useSquads } from '../stores/useSquads'
import { NotificationMessages } from './notificationMessages'
import { supabase } from './supabase'

// ── Types ──────────────────────────────────────────────────────────────────────

export type NudgeId = 'idle-signals' | 'focus-gap' | 'midday-snr' | 'eod-review' | 'squad-accountability'

// ── Constants ──────────────────────────────────────────────────────────────────

const STORAGE_KEY             = 'lockin_nudges_fired'        // JSON: { [dateKey]: NudgeId[] }
const SQUAD_NUDGE_STORAGE_KEY = 'lockin_squad_nudges_fired'  // JSON: { [memberDateHourKey]: true }
const WORKDAY_START           = 9                            // 09:00
const CHECK_INTERVAL          = 60_000                       // 1 minute
const SQUAD_SESSION_THRESHOLD = 60 * 60 * 1000              // 1 hour in ms

// ── Web Notifications helper ──────────────────────────────────────────────────

/**
 * Request notification permission (no-op if already granted/denied).
 * Returns true if we have permission to send.
 */
async function ensurePermission(): Promise<boolean> {
  if (!('Notification' in window)) return false
  if (Notification.permission === 'granted') return true
  if (Notification.permission === 'denied') return false
  const result = await Notification.requestPermission()
  return result === 'granted'
}

async function sendNudge(title: string, body: string): Promise<void> {
  try {
    const allowed = await ensurePermission()
    if (!allowed) return
    // eslint-disable-next-line no-new
    new Notification(title, { body, icon: '/icon-192.png', silent: false })
  } catch (err) {
    console.warn(`[nudge] send failed — ${title}:`, err)
  }
}

// ── localStorage helpers ───────────────────────────────────────────────────────

function todayKey(): string {
  return new Date().toISOString().split('T')[0]
}

function getFiredToday(): Set<NudgeId> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const all: Record<string, NudgeId[]> = raw ? JSON.parse(raw) : {}
    return new Set<NudgeId>(all[todayKey()] ?? [])
  } catch {
    return new Set()
  }
}

function markFired(id: NudgeId): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const all: Record<string, NudgeId[]> = raw ? JSON.parse(raw) : {}
    const today = todayKey()
    const fired = new Set<NudgeId>(all[today] ?? [])
    fired.add(id)
    all[today] = [...fired]

    // Prune entries older than 7 days to keep storage small
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - 7)
    const cutoffStr = cutoff.toISOString().split('T')[0]
    for (const key of Object.keys(all)) {
      if (key < cutoffStr) delete all[key]
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch { /* ignore */ }
}

// ── Squad accountability nudge helpers ────────────────────────────────────────

/**
 * Key format: `{userId}-{date}-{hour}` — ensures we fire at most once per
 * member per hour.
 */
function squadNudgeKey(memberId: string): string {
  const now = new Date()
  const date = now.toISOString().split('T')[0]
  const hour = now.getHours()
  return `${memberId}-${date}-${hour}`
}

function hasSquadNudgeFired(memberId: string): boolean {
  try {
    const raw = localStorage.getItem(SQUAD_NUDGE_STORAGE_KEY)
    const all: Record<string, true> = raw ? JSON.parse(raw) : {}
    return !!all[squadNudgeKey(memberId)]
  } catch {
    return false
  }
}

function markSquadNudgeFired(memberId: string): void {
  try {
    const raw = localStorage.getItem(SQUAD_NUDGE_STORAGE_KEY)
    const all: Record<string, true> = raw ? JSON.parse(raw) : {}
    all[squadNudgeKey(memberId)] = true

    // Prune old keys — keep only today's entries
    const today = new Date().toISOString().split('T')[0]
    for (const key of Object.keys(all)) {
      if (!key.includes(today)) delete all[key]
    }

    localStorage.setItem(SQUAD_NUDGE_STORAGE_KEY, JSON.stringify(all))
  } catch { /* ignore */ }
}

/**
 * Check squad presence and fire an accountability nudge if:
 *   - smart_nudges is enabled
 *   - the current user has no active focus session (focus_task is null)
 *   - at least one squad member has been in a session for >= 1 hour
 *   - we haven't already nudged about that member this hour
 */
async function checkSquadAccountabilityNudge(): Promise<void> {
  const { settings } = useSettings.getState()
  if (!(settings as any).smart_nudges) return

  const { data: { session } } = await supabase.auth.getSession()
  if (!session) return

  const { squadPresence, memberName } = useSquads.getState()
  if (!squadPresence.length) return

  // Check if current user is already focusing
  const myPresence = squadPresence.find(p => p.user_id === session.user.id)
  if (myPresence?.focus_task) return  // user is in a session — no nudge needed

  const now = Date.now()

  for (const presence of squadPresence) {
    if (presence.user_id === session.user.id) continue
    if (!presence.focus_task) continue

    const sessionAge = now - new Date(presence.updated_at).getTime()
    if (sessionAge < SQUAD_SESSION_THRESHOLD) continue

    if (hasSquadNudgeFired(presence.user_id)) continue

    const name = memberName(presence.user_id)
    const msg = NotificationMessages.squadAccountability(name, presence.focus_task)
    await sendNudge(msg.title, msg.body)
    markSquadNudgeFired(presence.user_id)

    // Fire one nudge per check cycle — don't spam if multiple members qualify
    break
  }
}

// ── Core check (evaluates all 5 nudge conditions) ────────────────────────────

async function checkNudges(): Promise<void> {
  const { settings } = useSettings.getState()
  if (!(settings as any).smart_nudges) return

  const { tasks } = useTasks.getState()
  const { snrPct } = useSNR.getState()

  const now  = new Date()
  const hour = now.getHours() + now.getMinutes() / 60

  const signals  = tasks.filter(t => t.type === 'signal' && !t.done)
  // PWA tasks don't have timer_running; treat as never focusing for nudge purposes
  const focusing = (tasks as any[]).some(t => t.timer_running)
  const workdayEnd = WORKDAY_START + ((settings as any).workday_hrs ?? 8)

  const alreadyFired = getFiredToday()

  // ── 1. Idle signals (10:00–11:00) ─────────────────────────────────────────
  if (
    !alreadyFired.has('idle-signals') &&
    hour >= 10 && hour < 11 &&
    signals.length > 0 &&
    !focusing
  ) {
    const msg = NotificationMessages.idleSignals(signals.length)
    await sendNudge(msg.title, msg.body)
    markFired('idle-signals')
  }

  // ── 2. Focus gap (11:00–12:00, i.e. 2h into 9am workday) ─────────────────
  const focusGapHour = WORKDAY_START + 2
  if (
    !alreadyFired.has('focus-gap') &&
    hour >= focusGapHour && hour < focusGapHour + 1 &&
    signals.length > 0 &&
    !focusing
  ) {
    const hasAnyProgress = (signals as any[]).some(t => (t.timer_seconds ?? 0) > 0)
    if (!hasAnyProgress) {
      const msg = NotificationMessages.focusGap()
      await sendNudge(msg.title, msg.body)
      markFired('focus-gap')
    }
  }

  // ── 3. Midday SNR (12:00–13:00) ───────────────────────────────────────────
  if (
    !alreadyFired.has('midday-snr') &&
    hour >= 12 && hour < 13 &&
    signals.length > 0 &&
    snrPct < ((settings as any).snr_notification_threshold ?? 75)
  ) {
    const msg = NotificationMessages.midDaySNR(snrPct)
    await sendNudge(msg.title, msg.body)
    markFired('midday-snr')
  }

  // ── 4. End-of-day review ((workdayEnd - 1h) to workdayEnd) ────────────────
  const eodHour = workdayEnd - 1
  if (
    !alreadyFired.has('eod-review') &&
    hour >= eodHour && hour < workdayEnd
  ) {
    const msg = NotificationMessages.eodReview()
    await sendNudge(msg.title, msg.body)
    markFired('eod-review')
  }

  // ── 5. Squad accountability (any time, hour-rate-limited per member) ───────
  await checkSquadAccountabilityNudge()
}

// ── Scheduler ──────────────────────────────────────────────────────────────────

let _interval: ReturnType<typeof setInterval> | null = null

/**
 * Start the nudge scheduler. Idempotent — safe to call multiple times.
 * Call once after the user is authenticated and app state is loaded.
 */
export function initNudgeScheduler(): void {
  if (_interval) return

  // Run immediately in case we're already inside a nudge window
  checkNudges().catch(() => {})

  _interval = setInterval(() => {
    checkNudges().catch(() => {})
  }, CHECK_INTERVAL)
}

/** Stop the scheduler (call on sign-out or app teardown). */
export function stopNudgeScheduler(): void {
  if (_interval) {
    clearInterval(_interval)
    _interval = null
  }
}

export function isNudgeSchedulerRunning(): boolean {
  return _interval !== null
}
